<div [class]="'flex flex-col gap-(--space-6) w-full ' + dialogMaxWidth + ' mx-auto'">
  <div class="flex flex-col gap-1">
    <label for="curve-name" id="dialog-title" class="text-sm font-medium text-[var(--text-color)]">{{
      'common.labels.name' | transloco
    }}</label>
    <input
      pInputText
      id="curve-name"
      [ngModel]="name()"
      (ngModelChange)="name.set($event)"
      [placeholder]="'graphs.dialog.namePlaceholder' | transloco"
      [attr.aria-invalid]="name() && !isValid()"
      [attr.aria-describedby]="name() && !isValid() ? 'curve-name-error' : null"
      class="w-full" />
    @if (name() && !isValid()) {
      <small id="curve-name-error" class="text-(color:--p-error-text) text-xs mt-1" role="alert">
        {{ getErrorMessageKey() | transloco }}
      </small>
    }
  </div>

  <div class="flex flex-col sm:flex-row gap-(--space-4)">
    <div class="flex flex-col gap-1">
      <label for="color-select" class="text-sm font-medium text-[var(--text-color)]">{{
        'graphs.dialog.colorLabel' | transloco
      }}</label>
      <div class="flex items-center gap-2">
        <div class="pipette-wrapper">
          <p-colorPicker
            [ngModel]="selectedColor()"
            (ngModelChange)="selectedColor.set($event)"
            [inline]="false"
            format="hex"
            appendTo="body"
            [attr.aria-label]="'graphs.dialog.customColor' | transloco" />
          <span class="material-symbols pipette-icon" aria-hidden="true">palette</span>
        </div>
        <p-select
          inputId="color-select"
          [options]="colorOptionsWithCustom()"
          [ngModel]="selectedColor()"
          (ngModelChange)="selectedColor.set($event)"
          optionValue="value"
          appendTo="body"
          [styleClass]="dropdownSizes.color"
          [panelStyleClass]="dropdownPanelSizes.color"
          [attr.aria-label]="getColorAriaLabel()">
          <ng-template let-option pTemplate="selectedItem">
            <div class="flex items-center justify-center h-full">
              <span
                class="inline-block rounded border border-[var(--border)]"
                [class]="colorSwatchSize.combined"
                [style.background-color]="option.value"
                [attr.aria-label]="option.labelKey | transloco"></span>
            </div>
          </ng-template>
          <ng-template let-option pTemplate="item">
            <div class="flex items-center justify-center py-2 w-full">
              <span
                class="inline-block rounded border border-[var(--border)]"
                [class]="colorSwatchSize.combined"
                [style.background-color]="option.value"
                [attr.aria-label]="option.labelKey | transloco"></span>
            </div>
          </ng-template>
        </p-select>
      </div>
    </div>

    <div class="flex flex-col gap-1">
      <label for="marker-symbol" class="text-sm font-medium text-[var(--text-color)]">{{
        'graphs.dialog.markerLabel' | transloco
      }}</label>
      <p-select
        inputId="marker-symbol"
        [options]="markerSymbolOptions"
        [ngModel]="markerSymbol()"
        (ngModelChange)="markerSymbol.set($event)"
        optionValue="value"
        appendTo="body"
        [styleClass]="dropdownSizes.marker"
        [panelStyleClass]="dropdownPanelSizes.marker"
        [attr.aria-label]="getMarkerAriaLabel()">
        <ng-template let-option pTemplate="selectedItem">
          <div class="flex items-center justify-center h-full">
            @if (isMarkerNone(option.value)) {
              <span class="text-sm">{{ option.labelKey | transloco }}</span>
            } @else {
              <span class="text-4xl font-bold leading-none" aria-hidden="true">{{ option.symbol }}</span>
            }
          </div>
        </ng-template>
        <ng-template let-option pTemplate="item">
          <div class="flex items-center justify-center py-2 w-full">
            @if (isMarkerNone(option.value)) {
              <span class="text-sm">{{ option.labelKey | transloco }}</span>
            } @else {
              <span class="text-4xl font-bold leading-none" aria-hidden="true">{{ option.symbol }}</span>
            }
          </div>
        </ng-template>
      </p-select>
    </div>

    <div class="flex flex-col gap-1 flex-1">
      <label for="line-type" class="text-sm font-medium text-[var(--text-color)]">{{
        'graphs.dialog.lineTypeLabel' | transloco
      }}</label>
      <p-select
        inputId="line-type"
        [options]="lineTypeOptions"
        [ngModel]="lineType()"
        (ngModelChange)="lineType.set($event)"
        optionValue="value"
        appendTo="body"
        [styleClass]="dropdownSizes.lineType"
        [panelStyleClass]="dropdownPanelSizes.lineType"
        [attr.aria-label]="getLineTypeAriaLabel()">
        <ng-template let-option pTemplate="selectedItem">
          <div class="flex items-center justify-center h-full">
            @if (isLineNone(option.value)) {
              <span class="text-sm">{{ option.labelKey | transloco }}</span>
            } @else {
              <svg width="48" height="12" aria-hidden="true">
                <line
                  x1="0"
                  y1="6"
                  x2="48"
                  y2="6"
                  stroke="currentColor"
                  stroke-width="3"
                  [attr.stroke-dasharray]="getLineDashArray(option.value)" />
              </svg>
            }
          </div>
        </ng-template>
        <ng-template let-option pTemplate="item">
          <div class="flex items-center justify-center py-2 w-full">
            @if (isLineNone(option.value)) {
              <span class="text-sm">{{ option.labelKey | transloco }}</span>
            } @else {
              <svg width="48" height="12" aria-hidden="true">
                <line
                  x1="0"
                  y1="6"
                  x2="48"
                  y2="6"
                  stroke="currentColor"
                  stroke-width="3"
                  [attr.stroke-dasharray]="getLineDashArray(option.value)" />
              </svg>
            }
          </div>
        </ng-template>
      </p-select>
    </div>

    <div class="flex flex-col gap-1 flex-1">
      <label for="thickness" class="text-sm font-medium text-[var(--text-color)]">{{
        'graphs.dialog.thicknessLabel' | transloco
      }}</label>
      <p-select
        inputId="thickness"
        [options]="thicknessOptions"
        [ngModel]="thickness()"
        (ngModelChange)="thickness.set($event)"
        optionLabel="label"
        optionValue="value"
        appendTo="body"
        [styleClass]="dropdownSizes.thickness"
        [panelStyleClass]="dropdownPanelSizes.thickness"
        [attr.aria-label]="getThicknessAriaLabel()">
        <ng-template let-option pTemplate="selectedItem">
          <div class="flex items-center gap-2">
            <span
              class="inline-block w-12 h-0 border-t border-solid border-[var(--text-color)]"
              [style.border-top-width.px]="option.value"
              aria-hidden="true"></span>
            <span>{{ option.value }}</span>
          </div>
        </ng-template>
        <ng-template let-option pTemplate="item">
          <div class="flex items-center justify-center gap-2">
            <span
              class="inline-block w-12 h-0 border-t border-solid border-[var(--text-color)]"
              [style.border-top-width.px]="option.value"
              aria-hidden="true"></span>
            <span>{{ option.value }}</span>
          </div>
        </ng-template>
      </p-select>
    </div>
  </div>

  <ces-dialog-footer
    [submitDisabled]="!isValid()"
    [submitTooltip]="!isValid() ? ('common.tooltips.formIncomplete' | transloco) : ''"
    [submitAriaLabel]="getValidateButtonAriaLabel()"
    (onSubmit)="save()"
    (onCancel)="cancel()" />
</div>
